import * as React from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * One status vocabulary for the whole app. Map the domain state to a tone at
 * the call site (paid -> success, overdue -> danger, draft -> neutral, ...);
 * the colours come from the theme tokens and clear 4.5:1 in every theme.
 */
export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral" | "accent"

export const statusToneClasses: Record<StatusTone, string> = {
  success: "border-success-on-surface/30 bg-success-muted text-success-on-surface",
  warning: "border-warning-on-surface/30 bg-warning-muted text-warning-on-surface",
  danger: "border-destructive-on-surface/30 bg-destructive-muted text-destructive-on-surface",
  info: "border-info-on-surface/30 bg-info-muted text-info-on-surface",
  neutral: "border-border bg-muted text-muted-foreground",
  accent: "border-primary/40 bg-primary/10 text-foreground",
}

/** Dot colour per tone; `accent` text is plain foreground, so its dot carries the brand colour. */
const dotClasses: Record<StatusTone, string> = {
  success: "bg-current",
  warning: "bg-current",
  danger: "bg-current",
  info: "bg-current",
  neutral: "bg-current",
  accent: "bg-primary",
}

const sizeClasses = {
  sm: { root: "h-5 gap-1 px-2 text-xs", icon: "size-3", dot: "size-1.5" },
  md: { root: "h-6 gap-1.5 px-2.5 text-sm", icon: "size-3.5", dot: "size-2" },
} as const

export interface StatusBadgeProps {
  tone: StatusTone
  /** The visible status text. Required: the tone colour is never the only cue (WCAG 1.4.1). */
  label: React.ReactNode
  /** Leading icon; without one a small dot marks the badge as a status. */
  icon?: LucideIcon
  size?: keyof typeof sizeClasses
  className?: string
  title?: string
}

function StatusBadge({ tone, label, icon: Icon, size = "sm", className, title }: StatusBadgeProps) {
  const s = sizeClasses[size]
  return (
    <span
      title={title}
      className={cn(
        "inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-full border font-medium",
        s.root,
        statusToneClasses[tone],
        className
      )}
    >
      {Icon ? (
        <Icon className={cn("shrink-0", s.icon)} aria-hidden="true" />
      ) : (
        <span className={cn("shrink-0 rounded-full", s.dot, dotClasses[tone])} aria-hidden="true" />
      )}
      <span className="truncate">{label}</span>
    </span>
  )
}

export { StatusBadge }
